import { FC, useState, useCallback, useMemo } from 'react';
import isEqual from 'lodash-es/isEqual';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { Button, PopoverDialog, usePopoverDialog, Dot } from '@frontend/design-system';
import { DEFAULT_FILTER_VALUES } from '../../constants';
import { taskCenterTrackingIds } from '../../tracking-ids';
import { SelectedFilters } from '../../types';
import { filtersIndicatorStyle, filtersContainerStyle } from './common-styles';
import { popoverStyle, applyButtonStyle } from './task-filters.styles';
import { TaskPersonFilter } from './task-person-filter';
import { TaskPopoverHeading } from './task-popover-heading';
import { TaskSortOrder } from './task-sort-order';
import { TaskSourceFilter } from './task-source-filter';

export const TaskFilters: FC = () => {
  const { selectedFilters, setFilters: setStoreFilters } = useTaskCenterStore(['selectedFilters', 'setFilters']);
  const [filters, setFilters] = useState<SelectedFilters>({ ...selectedFilters });
  const { t } = useTranslation('task-center');
  const popoverDialogProps = usePopoverDialog({ placement: 'bottom-start' });

  const disableFiltersClear = !filters.createdBy && !filters.source;

  const globalFilterApplied = useMemo(() => {
    const { createdBy, sortType, source } = selectedFilters;
    return !!(createdBy || sortType || source);
  }, [selectedFilters]);
  // Filters Clear functions
  const onClearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTER_VALUES, sortType: filters.sortType });
  }, [setFilters, filters.sortType]);
  const onClearSort = useCallback(() => {
    setFilters({ ...filters, sortType: DEFAULT_FILTER_VALUES.sortType });
  }, [setFilters, filters]);

  // Filters data update functions
  const setCreatedBy = useCallback((createdBy: string) => setFilters({ ...filters, createdBy }), [filters]);
  const setSource = useCallback((source: TaskCenterTypes.TaskSource) => setFilters({ ...filters, source }), [filters]);
  const setSortOrder = useCallback(
    (sortType: TaskCenterTypes.SortField | null) => setFilters({ ...filters, sortType }),
    [filters]
  );

  // Apply button functions
  const disableApply = isEqual(selectedFilters, filters);
  const onClickApply = useCallback(() => {
    setStoreFilters(filters);
    popoverDialogProps.close();
  }, [filters, setStoreFilters]);

  return (
    <span css={filtersContainerStyle}>
      <Button
        {...popoverDialogProps.getTriggerProps()}
        variant='secondary'
        iconName='filter-alt'
        size='large'
        hoverLabel={t('Filters')}
        trackingId={taskCenterTrackingIds.filters.otherFilters}
      />
      {globalFilterApplied && <Dot css={filtersIndicatorStyle} color='primary' />}
      <PopoverDialog {...popoverDialogProps.getDialogProps()} css={popoverStyle}>
        <TaskPopoverHeading
          label={t('Sort Order')}
          onClear={onClearSort}
          disableClear={!filters.sortType}
          trackingId={taskCenterTrackingIds.filters.clearSortFiltersButton}
        />
        <TaskSortOrder sortOrder={filters.sortType} setSortOrder={setSortOrder} />
        <TaskPopoverHeading
          label={t('Other Filters')}
          onClear={onClearFilters}
          disableClear={disableFiltersClear}
          trackingId={taskCenterTrackingIds.filters.clearOtherFiltersButton}
        />
        <TaskPersonFilter selectedValue={filters.createdBy} setValue={setCreatedBy} label={t('Created By')} />
        <TaskSourceFilter source={filters.source} setSource={setSource} />
        <Button
          onClick={onClickApply}
          disabled={disableApply}
          css={applyButtonStyle}
          trackingId={taskCenterTrackingIds.filters.applyOtherFiltersButton}
        >
          {t('Apply')}
        </Button>
      </PopoverDialog>
    </span>
  );
};
